import { IFluidObject } from '@fluidframework/core-interfaces';
import { PostMessageSender, PostMessageHandlers } from '../Shared/PostMessage';
import {
  LastEditChangedMessage,
  LastEditedComponentLoadedMessage,
  FromGuestLastEditedMessageType
} from '../MessageTypes/LastEditedMessageTypes';
import { ILastEditDetails } from '../MessageTypes/LastEditedDefinitions';
import { IComponentLastEditedTracker } from '@fluidx/office-fluid-types';
import { LastEditedTracker } from '@fluidx/utilities';

/**
 * Initializes everything needed to handle the LastEditedTracker interface outside the iframe.
 * @returns A dummy component with just this interface and the set of message handlers this interface is expecting
 */
export function getHostSideLastEdited(): {
  component: IComponentLastEditedTracker;
  handlers: PostMessageHandlers<string>;
  dispose: () => void;
} {
  let storedLastEditDetails: ILastEditDetails | undefined;

  const component: IComponentLastEditedTracker = {
    get IComponentLastEditedTracker(): IComponentLastEditedTracker {
      return this;
    },
    getLastEditDetails: () => storedLastEditDetails
  };

  const lastEditedTracker = new LastEditedTracker(Promise.resolve(component));

  const handlers: PostMessageHandlers<string> = {
    ['LastEditChanged']: (message) => {
      const { lastEditDetails } = (message as LastEditChangedMessage).payload;
      // Store the value.
      storedLastEditDetails = lastEditDetails;
      // Emit the event further so we know when to fetch this information.
      lastEditedTracker.emit('lastEditChanged');
    },
    ['LastEditedComponentLoaded']: (message) => {
      const { lastEditDetails } = (message as LastEditedComponentLoadedMessage).payload;
      // Store the value.
      storedLastEditDetails = lastEditDetails;
      // Emit the event further so we know when to fetch this information.
      lastEditedTracker.emit('lastEditedComponentLoaded');
    }
  };

  return {
    component: lastEditedTracker.IComponentLastEditedTracker,
    handlers,
    dispose: () => {
      lastEditedTracker.removeAllListeners();
    }
  };
}

/**
 * Initializes everything needed to handle the LastEditedTracker interface inside the iframe.
 * @param component the actual component with the real implementation of the interface
 * @param sender how to send messages to the outside of the iframe
 */
export function getGuestSideLastEdited(
  component: IFluidObject,
  sender: PostMessageSender
): PostMessageHandlers<string> | undefined {
  const lastEditedTracker = component.IComponentLastEditedTracker;
  if (!lastEditedTracker) {
    return undefined;
  }

  const sendLastEditedMessage = (messageType: FromGuestLastEditedMessageType): void => {
    const lastEditDetails = lastEditedTracker.getLastEditDetails();
    if (!lastEditDetails) {
      return;
    }

    // Send a message to the host to notify that the commandUpdated event has been emitted
    const updateMessage = {
      type: messageType,
      payload: { lastEditDetails }
    };

    sender.send(updateMessage);
  };

  if (lastEditedTracker.on) {
    lastEditedTracker.on('lastEditChanged', () => sendLastEditedMessage('LastEditChanged'));
    lastEditedTracker.on('lastEditedComponentLoaded', () => sendLastEditedMessage('LastEditedComponentLoaded'));
  }

  // Send the initial stored value on the other side of the iframe.
  sendLastEditedMessage('LastEditChanged');

  // We don't expect a 2 way communication so there's no handler for this side of iframe.
  return undefined;
}
